import { GMap } from '@components';
import { useGetRegionPolygon } from '@api';
import { useGetMapBounds, useSubPostPolygon } from '@hooks';
import { TGMapRegion, TMergeData, TRegionAddressItem } from '@types';
import {
	forwardRef, useEffect, useImperativeHandle, useMemo, useState
} from 'react';

type TSuburbPolygonEntry = {
	suburbId: string;
	raw: TMergeData;
	displayPaths: google.maps.LatLngLiteral[][];
};

export type RegionMapHandle = {
	addSuburb: (suburb: TRegionAddressItem) => void;
	removeSuburb: (id: string, postcodeInfo: { suburb: string; postcode: string; state?: string }) => void;
	removeAddedSuburb: (id: string) => void;
	isLoadingPolygon: () => boolean;
	getSaveData: () => {
		existingRaw: TGMapRegion | null;
		addedRaws: TMergeData[];
		deletedRaws: TMergeData[];
	};
};

type RegionMapProps = {
	externalId: string;
	isOpen: boolean;
};

const toLatLng = (ring: number[][]): google.maps.LatLngLiteral[] =>
	ring.map((coord) => ({ lat: coord[1], lng: coord[0] }));

const extractPolygons = (data: TGMapRegion): google.maps.LatLngLiteral[][][] => {
	const result: google.maps.LatLngLiteral[][][] = [];
	data.features.forEach((feature) => {
		const geom = feature.geometry;
		if (!geom) return;
		const leafGeometries = 'geometries' in geom ? geom.geometries : [geom];
		leafGeometries.forEach((g) => {
			if (g.type !== 'Polygon' && g.type !== 'MultiPolygon') return;
			if (!('coordinates' in g)) return;
			if (g.type === 'MultiPolygon') {
				(g.coordinates as number[][][][]).forEach((polygonRings) => {
					const rings = polygonRings.map(toLatLng);
					if (rings[0]?.length > 0) result.push(rings);
				});
			} else {
				const rings = (g.coordinates as number[][][]).map(toLatLng);
				if (rings[0]?.length > 0) result.push(rings);
			}
		});
	});
	return result;
};

export const RegionMap = forwardRef<RegionMapHandle, RegionMapProps>((props, ref) => {
	const { externalId, isOpen } = props;

	const [polygons, setPolygons] = useState<google.maps.LatLngLiteral[][][]>([]);
	const [addedPolygonEntries, setAddedPolygonEntries] = useState<TSuburbPolygonEntry[]>([]);
	const [deletedPolygonEntries, setDeletedPolygonEntries] = useState<TSuburbPolygonEntry[]>([]);
	const [existingRegionRaw, setExistingRegionRaw] = useState<TGMapRegion | null>(null);

	const { callGetRegionPolygon, loadingRegionPolygon } = useGetRegionPolygon();
	const { fetchSubPostPolygon } = useSubPostPolygon();
	const { bounds, getBounds } = useGetMapBounds();

	const addedDisplayPolygons = useMemo(
		() => addedPolygonEntries.flatMap((e) => e.displayPaths),
		[addedPolygonEntries]
	);

	const deletedDisplayPolygons = useMemo(
		() => deletedPolygonEntries.flatMap((e) => e.displayPaths),
		[deletedPolygonEntries]
	);

	useImperativeHandle(ref, () => ({
		addSuburb: (suburb) => {
			fetchSubPostPolygon([
				{
					suburb: suburb.suburb, postcode: suburb.postcode, state: suburb.state
				}
			]).then((data) => {
				if (!data) return;
				setAddedPolygonEntries((prev) => [
					...prev, {
						suburbId: suburb.id, raw: data.raw, displayPaths: data.polygons
					}
				]);
			});
		},
		removeSuburb: (id, postcodeInfo) => {
			fetchSubPostPolygon([postcodeInfo]).then((data) => {
				if (!data) return;
				setDeletedPolygonEntries((prev) => [
					...prev, {
						suburbId: id, raw: data.raw, displayPaths: data.polygons
					}
				]);
			});
		},
		removeAddedSuburb: (id) => {
			setAddedPolygonEntries((prev) => prev.filter((e) => e.suburbId !== id));
		},
		isLoadingPolygon: () => loadingRegionPolygon,
		getSaveData: () => ({
			existingRaw: existingRegionRaw,
			addedRaws: addedPolygonEntries.map((e) => e.raw),
			deletedRaws: deletedPolygonEntries.map((e) => e.raw),
		}),
	}), [existingRegionRaw, addedPolygonEntries, deletedPolygonEntries, loadingRegionPolygon]);

	useEffect(() => {
		if (!isOpen) {
			setPolygons([]);
			setAddedPolygonEntries([]);
			setDeletedPolygonEntries([]);
			setExistingRegionRaw(null);
			return;
		}
		setPolygons([]);
		const emptyRegion: TGMapRegion = { type: 'FeatureCollection', features: [] };
		callGetRegionPolygon(externalId).then((response) => {
			const raw = response?.data?.data;
			if (!raw) {
				setExistingRegionRaw(emptyRegion);
				return;
			}
			setExistingRegionRaw(raw);
			setPolygons(extractPolygons(raw));
		}).catch(() => {
			setExistingRegionRaw(emptyRegion);
		});
	}, [isOpen]);

	useEffect(() => {
		const allCoords = [...polygons.flat(), ...addedDisplayPolygons, ...deletedDisplayPolygons].flat();
		if (allCoords.length > 0) getBounds(allCoords);
	}, [polygons, addedDisplayPolygons, deletedDisplayPolygons]);

	return (
		<GMap
			loadingData={loadingRegionPolygon}
			bounds={bounds}
			mapContainerStyle={{ width: '100%', height: '270px' }}
			height={'270px'}
		>
			{polygons.map((paths, i) => <GMap.Polygon key={i} paths={paths} />)}
			{addedDisplayPolygons.length > 0 && (
				<GMap.Polygon paths={addedDisplayPolygons} options={{
					strokeColor: '#00A86B',
					fillColor: '#00A86B',
					fillOpacity: 0.35,
					strokeOpacity: 0.7,
					strokeWeight: 2,
				}} />
			)}
			{deletedDisplayPolygons.length > 0 && (
				<GMap.Polygon paths={deletedDisplayPolygons} options={{
					strokeColor: '#CC0003',
					fillColor: '#CC0003',
					fillOpacity: 0.5,
					strokeOpacity: 0.5,
					strokeWeight: 2,
				}} />
			)}
		</GMap>
	);
});
